import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/Loading.json';
import {
  Calendar, RefreshCw, TrendingUp, Wallet, CreditCard, Percent,
  CheckCircle2, Store, Clock, Layers,
  BarChart3, ListOrdered, Search, ChevronLeft, ChevronRight, ChevronDown,
} from 'lucide-react';
import { Dropdown } from '../components/ui/dropdown-01';
import { useSocket } from '../context/SocketContext';
import { PERMISSIONS, usePermissionHook } from '../utils/permissions';
import { getDefaultPageForUser } from '../utils/navigationHelper';
import { fetchPaymentKpi, fetchPaymentLogs, syncPayment, applyPaymentEvent } from '../redux/slices/paymentSlice';
import { api } from '../redux/api/axiosManager';

const CHART_COLORS = ['#1e9df1', '#00b87a', '#e6a5b8', '#8a79ab', '#f59e0b', '#ef4444', '#14b8a6'];
const CHANNEL_DONUT_COLORS = { app: '#8a79ab', direct: '#1e9df1', manual: '#f59e0b' };

const CHANNEL_LABELS = {
  app: 'App',
  direct: 'Web',
  manual: 'Manual',
};

// Özet KPI: yalnızca gerçek tahsilat kanalları
const SUMMARY_CHANNEL_KEYS = ['app', 'direct'];
const VALID_SUMMARY_CHANNEL_VALUES = ['all', ...SUMMARY_CHANNEL_KEYS];

// İşlem kayıtları: manual = Nebim'e işlenmemiş, sonradan elle tetiklenen işlemler
const LOG_CHANNEL_KEYS = ['app', 'direct', 'manual'];
const VALID_LOG_CHANNEL_VALUES = ['all', ...LOG_CHANNEL_KEYS];

const dateRangeOptions = [
  { value: 'today', label: 'Bugün' },
  { value: 'weekly', label: 'Son 7 gün' },
  { value: 'monthly', label: 'Son 30 gün' },
  { value: 'yearly', label: 'Son 365 gün' },
  { value: 'all', label: 'Tüm Zamanlar' },
];

// Frontend aralığı → backend payments/kpi dateRange sözleşmesi
function mapRangeToBackend(range) {
  switch (range) {
    case 'today': return 'today';
    case 'weekly': return 'last_7_days';
    case 'monthly': return 'last_30_days';
    case 'yearly': return 'last_365_days';
    case 'all': return 'all';
    default: return 'last_30_days';
  }
}

const formatTL = (n) =>
  `₺${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatCompactTL = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) {
    return `₺${(v / 1_000_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
  }
  if (v >= 1_000) {
    return `₺${(v / 1_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}K`;
  }
  return formatTL(v);
};
const formatNum = (n) => Number(n || 0).toLocaleString('tr-TR');
const formatPct = (n) =>
  `%${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;

// Saat etiketini "14:00" biçiminde göster
const formatHour = (h) => `${String(h).padStart(2, '0')}:00`;

// Log tablosu için tarih/saat gösterimi
const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return String(iso);
  }
};

// EventType → Türkçe etiket + renk
const EVENT_META = {
  PaymentStarted: { label: 'Başlatıldı', cls: 'bg-slate-100 text-slate-600' },
  PaymentRequestInitiated: { label: 'İstek Oluşturuldu', cls: 'bg-slate-100 text-slate-600' },
  SendingPaymentRequest: { label: 'İstek Gönderildi', cls: 'bg-slate-100 text-slate-600' },
  StartingIntegrator: { label: 'Entegrasyon Başladı', cls: 'bg-indigo-50 text-indigo-600' },
  PaymentCompleted: { label: 'Tamamlandı', cls: 'bg-emerald-50 text-emerald-700' },
  PaymentIntegrated: { label: 'Nebim Kaydı', cls: 'bg-blue-50 text-blue-700' },
  NebimIntegrationQueued: { label: 'Nebim Kuyruğa Alındı', cls: 'bg-violet-50 text-violet-700' },
  NebimIntegrationRetryScheduled: { label: 'Nebim Yeniden Deneme', cls: 'bg-amber-50 text-amber-700' },
  NebimIntegrationRetryExhausted: { label: 'Nebim Deneme Tükendi', cls: 'bg-rose-50 text-rose-700' },
  PaymentReconciliation: { label: 'Ödeme Mutabakatı', cls: 'bg-teal-50 text-teal-700' },
  PaymentStatusMismatch: { label: 'Tahsil Edildi, Durum Hatalı', cls: 'bg-amber-50 text-amber-700' },
  PaymentFailed: { label: 'Başarısız', cls: 'bg-rose-50 text-rose-700' },
  DuplicatePaymentIgnored: { label: 'Mükerrer', cls: 'bg-amber-50 text-amber-700' },
  PaymentIntegrationFailed: { label: 'Entegrasyon Hatası', cls: 'bg-rose-50 text-rose-700' },
  Service_PaymentIntegrationFailed: { label: 'Servis Hatası', cls: 'bg-rose-50 text-rose-700' },
  NebimManualActionRequired: { label: 'Manuel Nebim Kaydı Gerekiyor', cls: 'bg-orange-50 text-orange-700' },
};
const eventMeta = (t) => EVENT_META[t] || { label: t || '-', cls: 'bg-gray-100 text-gray-600' };

const SUMMARY_CHANNEL_OPTIONS = [
  { value: 'all', label: 'Tümü' },
  ...SUMMARY_CHANNEL_KEYS.map((key) => ({ value: key, label: CHANNEL_LABELS[key] })),
];

// API / DB kanal adlarını tek anahtara indirger (app | direct)
function normalizeChannelKey(name) {
  if (!name) return '';
  const k = String(name).trim().toLowerCase();
  if (k === 'app') return 'app';
  if (k === 'direct' || k === 'web') return 'direct';
  return k;
}

function channelDisplayLabel(key) {
  return CHANNEL_LABELS[key] || key || '-';
}

// channels[] satırını summary ile uyumlu KPI objesine çevirir
function channelRowToSummary(row) {
  if (!row) return {};
  return {
    totalAmount: row.totalAmount ?? row.amount ?? 0,
    totalCommission: row.totalCommission ?? 0,
    completedCount: row.completedCount ?? row.count ?? 0,
    failedCount: row.failedCount ?? 0,
    startedCount: row.startedCount ?? 0,
    conversionRate: row.conversionRate ?? 0,
    transactionSuccessRate: row.transactionSuccessRate ?? 0,
  };
}

function findChannelRow(channels, channelKey) {
  return (channels || []).find((c) => normalizeChannelKey(c.channel) === channelKey);
}

function Payments() {
  const dispatch = useDispatch();
  const { socket } = useSocket();
  const canViewPayments = usePermissionHook(PERMISSIONS.PAYMENTS_VIEW);
  const { user } = useSelector((state) => state.auth);
  const { data, loading, error, filterContext } = useSelector((state) => state.payment);
  const logEventTypes = useSelector((state) => state.payment.logs.eventTypes);
  const logChannels = useSelector((state) => state.payment.logs.channels);

  // Tarih aralığı oturum boyunca hatırlanır; yeni oturumda (yeni sekme/tarayıcı) her zaman 'today' ile açılır
  const getStoredRange = () => {
    if (typeof window === 'undefined') return 'today';
    const stored = sessionStorage.getItem('payment_dateRange');
    const valid = dateRangeOptions.map((o) => o.value);
    return stored && valid.includes(stored) ? stored : 'today';
  };
  const getStoredLogEventType = () => {
    if (typeof window === 'undefined') return 'all';
    return localStorage.getItem('payment_log_eventType') || 'all';
  };
  const getStoredLogChannel = () => {
    if (typeof window === 'undefined') return 'all';
    const stored = localStorage.getItem('payment_log_channel');
    return stored && VALID_LOG_CHANNEL_VALUES.includes(stored) ? stored : 'all';
  };
  const getStoredSummaryChannel = () => {
    if (typeof window === 'undefined') return 'all';
    const stored = localStorage.getItem('payment_summaryChannel');
    if (stored === 'manual') return 'all';
    return stored && VALID_SUMMARY_CHANNEL_VALUES.includes(stored) ? stored : 'all';
  };
  const [dateRange, setDateRange] = useState(() => getStoredRange());
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined') return 'summary';
    const stored = localStorage.getItem('payment_activeTab');
    return stored === 'logs' || stored === 'summary' ? stored : 'summary';
  }); // 'summary' | 'logs'
  const [eventType, setEventType] = useState(() => getStoredLogEventType());
  const [channel, setChannel] = useState(() => getStoredLogChannel());
  const [summaryChannel, setSummaryChannel] = useState(() => getStoredSummaryChannel());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const submitSearch = () => setSearch(searchInput.trim());

  const eventTypeItems = useMemo(() => {
    const items = [
      { value: 'all', label: 'Tüm Olaylar' },
      ...logEventTypes.map((t) => ({ value: t, label: eventMeta(t).label })),
    ];
    if (eventType !== 'all' && !logEventTypes.includes(eventType)) {
      items.push({ value: eventType, label: eventMeta(eventType).label });
    }
    return items;
  }, [logEventTypes, eventType]);

  const channelItems = useMemo(() => {
    const BASE = LOG_CHANNEL_KEYS.map((key) => ({ value: key, label: CHANNEL_LABELS[key] }));
    const fromKpi = (data?.channels || [])
      .map((c) => normalizeChannelKey(c.channel))
      .filter((k) => LOG_CHANNEL_KEYS.includes(k));
    const fromLogs = Array.isArray(logChannels)
      ? logChannels.map((c) => normalizeChannelKey(typeof c === 'string' ? c : c.channel)).filter(Boolean)
      : [];
    const keys = Array.from(new Set([...LOG_CHANNEL_KEYS, ...fromKpi, ...fromLogs]));
    return [
      { value: 'all', label: 'Tüm Kanallar' },
      ...keys.map((k) => BASE.find((b) => b.value === k) || { value: k, label: channelDisplayLabel(k) }),
    ];
  }, [data?.channels, logChannels]);

  useEffect(() => {
    if (typeof window !== 'undefined') sessionStorage.setItem('payment_dateRange', dateRange);
  }, [dateRange]);

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem('payment_activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem('payment_summaryChannel', summaryChannel);
  }, [summaryChannel]);

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem('payment_log_eventType', eventType);
  }, [eventType]);

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem('payment_log_channel', channel);
  }, [channel]);

  const loadData = useCallback(
    (force = false) => {
      if (!canViewPayments) return;
      const backendRange = mapRangeToBackend(dateRange);
      const channelParam = summaryChannel !== 'all' ? summaryChannel : undefined;
      // Kanal/tarih değişiminde aynı ETag yanıtının karışmaması için cache temizle
      if (force || channelParam) api.clearETagCache('/payments/kpi');
      dispatch(fetchPaymentKpi({ dateRange: backendRange, channel: channelParam }));
    },
    [canViewPayments, dateRange, summaryChannel, dispatch]
  );

  // İlk yükleme + tarih değişimi → REST snapshot
  useEffect(() => {
    loadData(false);
    setLastUpdate(new Date());
  }, [loadData]);

  // WS payment:sync — yalnızca seçili aralık, backend push aralığıyla (last_30_days) eşleşiyorsa uygula
  useEffect(() => {
    if (!socket || !canViewPayments) return;
    const handleSync = (payload) => {
      if (payload?.type !== 'PAYMENT_SYNC' || !payload.data) return;
      const payloadRange = payload.filterContext?.dateRange;
      if (payloadRange && payloadRange !== mapRangeToBackend(dateRange)) return;
      const payloadChannel = normalizeChannelKey(payload.filterContext?.channel);
      const activeChannel = summaryChannel !== 'all' ? summaryChannel : '';
      if (activeChannel && payloadChannel && payloadChannel !== activeChannel) return;
      if (activeChannel && !payloadChannel) return;
      dispatch(syncPayment({ data: payload.data, filterContext: payload.filterContext }));
      setLastUpdate(new Date());
    };
    socket.on('payment:sync', handleSync);
    return () => socket.off('payment:sync', handleSync);
  }, [socket, canViewPayments, dateRange, summaryChannel, dispatch]);

  // WS payment:event — anlık delta (yalnızca tüm kanallar görünümünde)
  useEffect(() => {
    if (!socket || !canViewPayments || summaryChannel !== 'all') return;
    const handleEvent = (payload) => {
      if (!payload) return;
      dispatch(applyPaymentEvent(payload));
      setLastUpdate(new Date());
    };
    socket.on('payment:event', handleEvent);
    return () => socket.off('payment:event', handleEvent);
  }, [socket, canViewPayments, summaryChannel, dispatch]);

  const channels = data?.channels || [];
  const stores = data?.stores || [];
  const dailyTrend = data?.dailyTrend || [];
  const hourly = data?.hourly || [];
  const failReasons = useMemo(
    () => [...(data?.failReasons || [])].sort((a, b) => (b.count || 0) - (a.count || 0)),
    [data?.failReasons]
  );

  // Özet KPI: ?channel= ile filtrelenmiş yanıtta summary; aksi halde channels[] kırılımı
  const summary = useMemo(() => {
    if (!data) return {};
    if (summaryChannel === 'all') return data.summary || {};

    const ctxChannel = normalizeChannelKey(filterContext?.channel);
    if (ctxChannel === summaryChannel && data.summary) return data.summary;

    const row = findChannelRow(channels, summaryChannel);
    if (row) return channelRowToSummary(row);

    return data.summary || {};
  }, [data, summaryChannel, filterContext, channels]);

  const channelPieData = useMemo(
    () => channels
      .filter((c) => normalizeChannelKey(c.channel) !== 'manual')
      .map((c) => {
      const key = normalizeChannelKey(c.channel);
      return {
        key,
        name: channelDisplayLabel(key),
        value: c.amount ?? c.totalAmount ?? 0,
        color: CHANNEL_DONUT_COLORS[key] || CHART_COLORS[0],
      };
    }),
    [channels]
  );

  const channelPieTotal = useMemo(
    () => channelPieData.reduce((sum, c) => sum + (c.value || 0), 0),
    [channelPieData]
  );

  const summaryChannelItems = useMemo(
    () => SUMMARY_CHANNEL_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    []
  );

  // Backend ?channel= ile tüm yanıtı filtrelediğinde filterContext.channel döner
  const isApiChannelFiltered = useMemo(() => {
    if (summaryChannel === 'all') return true;
    return normalizeChannelKey(filterContext?.channel) === summaryChannel;
  }, [summaryChannel, filterContext]);

  const kpiCards = useMemo(() => [
    {
      icon: <Wallet size={24} />, label: 'Toplam Tahsilat',
      value: formatTL(summary.totalAmount), bg: 'bg-emerald-50', color: 'text-emerald-600',
    },
    {
      icon: <CreditCard size={24} />, label: 'Toplam Komisyon',
      value: formatTL(summary.totalCommission), bg: 'bg-purple-50', color: 'text-purple-600',
    },
    {
      icon: <Percent size={24} />, label: 'Dönüşüm Oranı',
      value: formatPct(summary.conversionRate), bg: 'bg-cyan-50', color: 'text-cyan-600',
      sub: `${formatNum(summary.completedCount)} / ${formatNum(summary.startedCount)} başlatılan`,
    },
    {
      icon: <CheckCircle2 size={24} />, label: 'İşlem Başarı Oranı',
      value: formatPct(summary.transactionSuccessRate), bg: 'bg-green-50', color: 'text-green-600',
      sub: `${formatNum(summary.completedCount)} başarılı · ${formatNum(summary.failedCount)} başarısız`,
    },
  ], [summary]);

  const isEmpty = !loading && (!data || summary.startedCount === undefined);

  if (!canViewPayments) {
    return <Navigate to={getDefaultPageForUser(user)} replace />;
  }

  return (
    <div className="space-y-4">
      {/* Header — başlık + sekmeler + yenile/tarih aynı kartta */}
      <div className="bg-white rounded-2xl p-5 mb-5 border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Ödeme Metrikleri</h1>
          <p className="text-gray-500 mt-2 text-base">Mobil ve web üzerinden alınan ödemelerin tahsilat, komisyon ve mağaza bazlı performansını görüntüleyin.</p>
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
            <Clock size={14} />
            <span>Son güncelleme: {lastUpdate.toLocaleTimeString('tr-TR')}</span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveTab('summary')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'summary' ? 'bg-emerald-50 text-emerald-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <BarChart3 size={16} /> Özet
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'logs' ? 'bg-emerald-50 text-emerald-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <ListOrdered size={16} /> İşlem Kayıtları
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => { loadData(true); setLastUpdate(new Date()); }}
              className="group flex items-center justify-center w-10 h-10 rounded-full bg-gray-50 text-gray-600 hover:bg-emerald-50 hover:text-emerald-600 transition-all duration-300 flex-shrink-0"
              title="Yenile"
            >
              <RefreshCw size={20} className="transform group-hover:rotate-180 transition-transform duration-500" />
            </button>
            {activeTab === 'summary' && (
              <Dropdown
                iconLeft={<Layers size={18} />}
                items={summaryChannelItems}
                value={summaryChannel}
                onChange={setSummaryChannel}
                simple
                className="min-w-[160px]"
              />
            )}
            <Dropdown
              iconLeft={<Calendar size={18} />}
              items={dateRangeOptions.map((opt) => ({ value: opt.value, label: opt.label }))}
              value={dateRange}
              onChange={setDateRange}
              simple
              className="min-w-[180px]"
            />
          </div>
        </div>
      </div>

      {/* İşlem Kayıtları filtreleri */}
      {activeTab === 'logs' && (
      <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitSearch()}
              placeholder="İsim, TC, cari kod, mağaza veya işlem no..."
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-300"
            />
          </div>
          <button
            onClick={submitSearch}
            className="px-4 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100 transition-colors"
          >
            Ara
          </button>
        </div>
        <div className="flex items-center gap-3 md:ml-auto">
          <Dropdown
            iconLeft={<ListOrdered size={18} />}
            items={eventTypeItems}
            value={eventType}
            onChange={setEventType}
            simple
            className="min-w-[180px]"
          />
          <Dropdown
            iconLeft={<Layers size={18} />}
            items={channelItems}
            value={channel}
            onChange={setChannel}
            simple
            className="min-w-[180px]"
          />
        </div>
      </div>
      )}

      {activeTab === 'summary' && (
      <>
      {error && (
        <div className="bg-red-50 border border-red-100 text-red-700 rounded-2xl px-5 py-4 text-sm">
          Ödeme verileri alınamadı: {typeof error === 'string' ? error : 'Bilinmeyen hata'}
        </div>
      )}

      {summaryChannel !== 'all' && !isApiChannelFiltered && !loading && (
        <div className="bg-amber-50 border border-amber-100 text-amber-800 rounded-2xl px-5 py-3 text-sm">
          {channelDisplayLabel(summaryChannel)} seçili; üst KPI kartları kanal kırılımından güncelleniyor.
          Günlük tahsilat, mağaza, saatlik dağılım ve başarısızlık sebepleri henüz kanala göre filtrelenmiyor — backend&apos;in
          {' '}
          <span className="font-medium">GET /payments/kpi?channel=app|direct</span>
          {' '}
          ile tüm alanları filtrelemesi gerekiyor.
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((card, index) => (
          <div
            key={index}
            className="group bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 relative overflow-hidden"
          >
            <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${card.bg.replace('bg-', 'from-')} to-white opacity-20 rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110 duration-500`} />
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-14 h-14 rounded-2xl ${card.bg} ${card.color} flex items-center justify-center shadow-inner`}>
                  {card.icon}
                </div>
              </div>
              <p className="text-sm font-medium text-gray-500 mb-1">{card.label}</p>
              {loading && !data ? (
                <div className="animate-pulse"><div className="h-8 bg-gray-100 rounded mb-2 w-1/2" /></div>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-gray-900 tracking-tight">{card.value}</h3>
                  {card.sub && <p className="text-xs text-gray-400 mt-1">{card.sub}</p>}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Günlük Tahsilat Trendi — 'Bugün' filtresinde toplamla aynı olduğu için gizlenir */}
        {dateRange !== 'today' && (
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-gray-900">Günlük Tahsilat</h3>
            <div className="p-3 bg-emerald-50 rounded-xl"><TrendingUp className="text-emerald-600" size={20} /></div>
          </div>
          {loading && !data ? (
            <div className="flex items-center justify-center h-[300px]"><Lottie animationData={loadingAnimation} loop className="w-28 h-28" /></div>
          ) : dailyTrend.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] text-sm text-gray-400">Seçili aralıkta tahsilat yok.</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dailyTrend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="paymentAmountGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00b87a" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#00b87a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={(d) => String(d).slice(5)} />
                <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <Tooltip
                  formatter={(value, name) => (name === 'amount' ? [formatTL(value), 'Tahsilat'] : [formatNum(value), 'İşlem'])}
                  labelFormatter={(l) => `Tarih: ${l}`}
                />
                <Area type="monotone" dataKey="amount" stroke="#00b87a" strokeWidth={2} fill="url(#paymentAmountGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        )}

        {/* Mağaza Kırılımı */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-gray-900">Mağaza Bazında Tahsilat</h3>
            <div className="p-3 bg-blue-50 rounded-xl"><Store className="text-blue-600" size={20} /></div>
          </div>
          {loading && !data ? (
            <div className="flex items-center justify-center h-[300px]"><Lottie animationData={loadingAnimation} loop className="w-28 h-28" /></div>
          ) : stores.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] text-sm text-gray-400">Mağaza verisi yok.</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stores} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: '#64748b' }} />
                <Tooltip formatter={(value) => [formatTL(value), 'Tahsilat']} />
                <Bar dataKey="amount" radius={[0, 6, 6, 0]}>
                  {stores.map((entry, index) => (
                    <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Kanal Dağılımı — tek kanal seçiliyken anlamsız */}
        {summaryChannel === 'all' && (
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Kanal Özeti</p>
              <h3 className="text-lg font-bold text-gray-900">Kanal Dağılımı</h3>
            </div>
            <div className="p-2.5 bg-purple-50 rounded-xl"><Layers className="text-purple-600" size={18} /></div>
          </div>
          {loading && !data ? (
            <div className="flex items-center justify-center h-[300px]"><Lottie animationData={loadingAnimation} loop className="w-28 h-28" /></div>
          ) : channelPieData.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] text-sm text-gray-400">Kanal verisi yok.</div>
          ) : (
            <>
              <div className="relative h-[220px] mt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={channelPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={72}
                      outerRadius={105}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {channelPieData.map((entry) => (
                        <Cell key={entry.key} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [formatTL(value), name]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-2xl font-bold text-gray-900 tracking-tight">{formatCompactTL(channelPieTotal)}</span>
                  <span className="text-xs text-gray-400 mt-0.5">Toplam</span>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4 pt-4 border-t border-gray-50">
                {channelPieData.map((entry) => (
                  <div key={entry.key} className="flex items-center gap-2 text-sm">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
                    <span className="text-gray-600">{entry.name}</span>
                    <span className="font-semibold text-gray-900">{formatTL(entry.value)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        )}

        {/* Saatlik Dağılım */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-gray-900">Saatlik İşlem Dağılımı</h3>
            <div className="p-3 bg-amber-50 rounded-xl"><Clock className="text-amber-600" size={20} /></div>
          </div>
          {loading && !data ? (
            <div className="flex items-center justify-center h-[300px]"><Lottie animationData={loadingAnimation} loop className="w-28 h-28" /></div>
          ) : hourly.every((h) => h.count === 0) ? (
            <div className="flex items-center justify-center h-[300px] text-sm text-gray-400">Saatlik veri yok.</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hourly} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="hour" tickFormatter={formatHour} tick={{ fontSize: 11, fill: '#94a3b8' }} interval={1} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <Tooltip formatter={(value) => [formatNum(value), 'İşlem']} labelFormatter={(h) => `Saat: ${formatHour(h)}`} />
                <Bar dataKey="count" fill="#f59e0b" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Alt bilgi tabloları */}
      <div className="grid grid-cols-1 gap-6">
        {/* Başarısızlık Sebepleri */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Başarısızlık Sebepleri</h3>
          {failReasons.length === 0 ? (
            <p className="text-sm text-gray-400">Başarısız işlem yok.</p>
          ) : (
            <div className="space-y-2">
              {failReasons.map((fr) => (
                <div key={fr.reason} className="flex items-center justify-between gap-4 py-2 border-b border-gray-50 last:border-0">
                  <span className="text-sm text-gray-600 truncate mr-4" title={fr.reason}>{fr.reason}</span>
                  <span className="text-sm font-semibold text-rose-600 flex-shrink-0">{formatNum(fr.count)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isEmpty && !error && (
        <div className="text-center text-sm text-gray-400 py-6">Seçili tarih aralığında ödeme verisi bulunamadı.</div>
      )}
      </>
      )}

      {activeTab === 'logs' && (
        <PaymentLogsPanel
          dateRange={dateRange}
          canView={canViewPayments}
          eventType={eventType}
          channel={channel}
          search={search}
        />
      )}
    </div>
  );
}

// JsonData string'ini okunur biçime getir (parse edilebiliyorsa girintili)
function parsePaymentLogJson(raw) {
  if (raw == null || raw === '') return null;
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
}

function tryParseEmbeddedJson(value) {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function normalizePaymentLogDetail(raw) {
  const parsed = parsePaymentLogJson(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return parsed;

  const normalized = { ...parsed };

  if (typeof normalized.IntegratorModel === 'string') {
    normalized.IntegratorModel = tryParseEmbeddedJson(normalized.IntegratorModel);
  }

  return normalized;
}

function resolvePaymentLogAmount(row) {
  if (row?.amount != null && row.amount !== '') {
    const direct = Number(row.amount);
    if (!Number.isNaN(direct)) return direct;
  }

  const payload = normalizePaymentLogDetail(row?.jsonData);
  if (!payload || typeof payload !== 'object') return null;

  const integratorPaymentAmount = payload.IntegratorModel?.Payment?.Payments?.[0]?.Amount;

  const candidates = [
    payload.PaymentAmount,
    payload.paymentAmount,
    payload.Amount,
    payload.amount,
    payload.Data?.PaymentAmount,
    payload.Data?.Amount,
    payload.Data?.amount,
    integratorPaymentAmount,
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue;
    const parsed = Number(String(candidate).replace(',', '.'));
    if (!Number.isNaN(parsed)) return parsed;
  }

  return null;
}

function prettyJson(raw) {
  if (raw == null) return '';
  try {
    const normalized = normalizePaymentLogDetail(raw);
    return JSON.stringify(normalized ?? raw, null, 2);
  } catch {
    return String(raw);
  }
}

/**
 * "İşlem Kayıtları" sekmesi — sp_BulutTahsilatPaymentEventLog_YK ham satırları.
 * Tarih (üstteki ortak filtre) + olay tipi + kanal + arama + sayfalama; chevron ile JsonData detayı.
 */
function PaymentLogsPanel({ dateRange, canView, eventType = 'all', channel = 'all', search = '' }) {
  const dispatch = useDispatch();
  const { rows, pagination, loading, error } = useSelector((state) => state.payment.logs);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const pageSize = 50;

  const backendRange = mapRangeToBackend(dateRange);

  // Tarih / olay tipi / kanal / arama / sayfa değişince kayıtları çek
  useEffect(() => {
    if (!canView) return;
    dispatch(fetchPaymentLogs({ dateRange: backendRange, eventType, channel, search, page, pageSize }));
  }, [dispatch, canView, backendRange, eventType, channel, search, page]);

  // Tarih / olay tipi / kanal / arama değişince başa dön
  useEffect(() => {
    setPage(1);
  }, [backendRange, eventType, channel, search]);

  // Aynı işlem no'ya (correlationId) sahip satırlar aynı renk grubuna girsin;
  // ardışık farklı işlemler dönüşümlü renk alsın (ödemeleri ayırt etmek için).
  const correlationParity = useMemo(() => {
    const map = new Map();
    let group = 0;
    rows.forEach((r) => {
      const key = r.correlationId;
      if (!key || map.has(key)) return;
      map.set(key, group % 2);
      group += 1;
    });
    return map;
  }, [rows]);

  const { page: curPage, totalPages, total } = pagination;

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-100 text-red-700 rounded-2xl px-5 py-4 text-sm">
          Kayıtlar alınamadı: {typeof error === 'string' ? error : 'Bilinmeyen hata'}
        </div>
      )}

      {/* Tablo */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center h-[300px]">
            <Lottie animationData={loadingAnimation} loop className="w-28 h-28" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center h-[240px] text-sm text-gray-400">
            Seçili aralıkta kayıt bulunamadı.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-700 text-sm tracking-wide">
                  <th className="text-left font-bold px-4 py-3 w-8"></th>
                  <th className="text-left font-bold px-4 py-3">İşlem No</th>
                  <th className="text-left font-bold px-4 py-3">Cari Kod</th>
                  <th className="text-left font-bold px-4 py-3">TC</th>
                  <th className="text-left font-bold px-4 py-3">İsim</th>
                  <th className="text-left font-bold px-4 py-3">Olay</th>
                  <th className="text-right font-bold px-4 py-3">Tutar</th>
                  <th className="text-left font-bold px-4 py-3">Kanal</th>
                  <th className="text-left font-bold px-4 py-3">Mağaza</th>
                  <th className="text-left font-bold px-4 py-3 whitespace-nowrap">Tarih</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const meta = eventMeta(row.eventType);
                  const isOpen = expanded === idx;
                  const logAmount = resolvePaymentLogAmount(row);
                  const prev = idx > 0 ? rows[idx - 1] : null;
                  const isNewGroup = !prev || prev.correlationId !== row.correlationId;
                  const parity = row.correlationId != null
                    ? (correlationParity.get(row.correlationId) ?? 0)
                    : idx % 2;
                  const rowBg = parity === 0 ? 'bg-white' : 'bg-emerald-50/60';
                  return (
                    <React.Fragment key={`${row.correlationId || 'row'}-${idx}`}>
                      <tr
                        className={`${rowBg} ${isNewGroup ? 'border-t-2 border-gray-200' : 'border-t border-transparent'} transition-colors`}
                      >
                        <td className="px-4 py-3 text-gray-400">
                          <button
                            type="button"
                            onClick={() => setExpanded(isOpen ? null : idx)}
                            className="p-1 rounded-md hover:bg-gray-200/80 text-gray-500 hover:text-gray-700 transition-colors"
                            aria-expanded={isOpen}
                            aria-label={isOpen ? 'Detayı kapat' : 'Detayı göster'}
                          >
                            <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-500 font-mono text-xs lowercase whitespace-nowrap">{row.correlationId || '-'}</td>
                        <td className="px-4 py-3 text-gray-600 font-mono text-xs whitespace-nowrap">{row.currAccCode || '-'}</td>
                        <td className="px-4 py-3 text-gray-500 font-mono text-xs">{row.identityNum || '-'}</td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.firstLastName || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2.5 py-1 rounded-lg text-xs font-medium ${meta.cls}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                          {logAmount != null ? formatTL(logAmount) : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {row.channel ? channelDisplayLabel(normalizeChannelKey(row.channel)) : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{row.storeName || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">{formatDateTime(row.operationDate)}</td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-gray-50/40">
                          <td colSpan={10} className="px-4 py-4">
                            <pre className="bg-gray-900 text-gray-100 rounded-xl p-4 text-xs overflow-x-auto max-h-80 overflow-y-auto">
                              {prettyJson(row.jsonData)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sayfalama */}
      {rows.length > 0 && (
        <div className="flex items-center justify-between bg-white rounded-2xl px-5 py-3 border border-gray-100 shadow-sm">
          <span className="text-sm text-gray-500">
            Sayfa {curPage} / {totalPages} • Toplam {formatNum(total)} kayıt
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={curPage <= 1 || loading}
              className="flex items-center gap-1 px-3 py-2 rounded-xl text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} /> Önceki
            </button>
            <button
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={curPage >= totalPages || loading}
              className="flex items-center gap-1 px-3 py-2 rounded-xl text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Sonraki <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Payments;
